import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import {KeyboardDatePicker} from '@material-ui/pickers';
import {Dialog} from '@mui/material';
import {isLoaded} from 'Core/models/Async';
import {
  clearCopyQuoteDetails,
  clearQuoteDeclineDetails,
  copyQuote,
  declineQuote,
  QuoteActionSource,
  toggleQuoteActionConfirmationModal,
  updateCopyQuoteDetails,
  updateQuoteDeclineDetails,
} from 'Quoting/ducks/quoteActions';
import {getISODateString, safeRenderDatePickerValue} from 'Quoting/utils/dateUtils';
import {getOpportunityURI, isDefined} from 'Quoting/utils/utilities';
import {DateTime} from 'luxon';
import React, {useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import './QuoteActionModals.sass';
import {QuoteListAction} from './QuoteActionUtils';
import {declineByCompanyReasons, declineByCustomerReasons} from './lookups';

export type QuotingToolModalProps = Partial<{
  onClose: (...args: any[]) => void;
  onContinue: (...args: any[]) => void;
  open: boolean;
  title: React.ReactNode;
  children: React.ReactNode;
  disableContinue: boolean;
}>;

export const QuotingToolModal = ({
  onClose,
  title,
  open,
  children,
  onContinue,
  disableContinue = false,
}: QuotingToolModalProps) => {
  return (
    <Dialog onClose={onClose} open={open ?? false} fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>{children}</DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" style={{textTransform: 'capitalize'}}>
          Cancel
        </Button>
        <Button
          disabled={disableContinue}
          onClick={() => {
            onContinue?.();
          }}
          color="primary"
          style={{textTransform: 'capitalize'}}
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const DeclineQuoteModal = (props: QuotingToolModalProps) => {
  const {selectedQuoteAction, selectedQuotesForAction, selectedSingleQuoteForAction, actionSource} = useSelector(
    (state: ReducerState) => state.quoteActions
  );
  const [declineDetails, setDeclineDetails] = useState<{
    declineDate: DateTime | null;
    declineOtherReason: string;
    declineReason: string;
  }>({
    declineDate: null,
    declineOtherReason: '',
    declineReason: '',
  });

  const options = useMemo(
    () =>
      selectedQuoteAction === QuoteListAction.DeclineByCustomer
        ? declineByCustomerReasons.map(o => ({label: o.displayValue, value: o.code}))
        : declineByCompanyReasons.map(o => ({label: o.displayValue, value: o.code})),
    [selectedQuoteAction]
  );

  const selectedQuotes =
    actionSource === QuoteActionSource.QuoteList && isDefined(selectedSingleQuoteForAction)
      ? [selectedSingleQuoteForAction]
      : Object.values(selectedQuotesForAction);

  const effectiveDateToCheck = selectedQuotes.length === 1 ? selectedQuotes[0]?.effectiveDate : undefined;

  const isDeclineDateOutOfQuoteTerm =
    isDefined(effectiveDateToCheck) && isDefined(declineDetails?.declineDate)
      ? declineDetails?.declineDate < DateTime.fromISO(effectiveDateToCheck)
      : false;

  const dispatch = useDispatch();
  const handleContinue = () => {
    dispatch(
      updateQuoteDeclineDetails({
        declineBy: selectedQuoteAction === QuoteListAction.DeclineByCustomer ? 'customer' : 'company',
        requestPayload: {...declineDetails, declineDate: getISODateString(declineDetails.declineDate)},
      })
    );

    dispatch(toggleQuoteActionConfirmationModal(true));
    props.onClose?.();
  };

  return (
    <QuotingToolModal
      {...props}
      onClose={() => {
        props.onClose?.();
        setDeclineDetails({declineDate: null, declineOtherReason: '', declineReason: ''});
      }}
      onContinue={() => {
        handleContinue();
        props.onClose?.();
      }}
      disableContinue={Boolean(
        !declineDetails.declineDate ||
          declineDetails.declineReason === '' ||
          isDeclineDateOutOfQuoteTerm ||
          (declineDetails.declineReason === 'other' && declineDetails.declineOtherReason === '')
      )}
    >
      <Grid container direction="column" spacing={4}>
        <Grid item xs={8}>
          <KeyboardDatePicker
            format={'MM/dd/yyyy'}
            placeholder="MM/DD/YYYY"
            variant="inline"
            id="decline-date"
            label="Date"
            onChange={date => setDeclineDetails({...declineDetails, declineDate: date})}
            value={safeRenderDatePickerValue(getISODateString(declineDetails?.declineDate))}
            required
            margin="dense"
            InputLabelProps={{shrink: true}}
            inputVariant="outlined"
            error={isDeclineDateOutOfQuoteTerm}
            helperText={
              isDeclineDateOutOfQuoteTerm ? 'Transaction Effective Date is outside of the quote term.' : undefined
            }
          />
        </Grid>
        <Grid item xs={8}>
          <InputLabel shrink id="decline-reason-label" style={{marginBottom: '0.15rem'}} required>
            Reason
          </InputLabel>
          <Select
            id="decline-reason"
            labelId="decline-reason-label"
            onChange={e => setDeclineDetails({...declineDetails, declineReason: e.target.value as string})}
            value={declineDetails.declineReason}
            required
            variant="outlined"
            margin="dense"
            fullWidth
          >
            {options.map(opt => (
              <MenuItem value={opt.value} key={opt.label}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item xs={8}>
          {declineDetails.declineReason === 'other' && (
            <TextField
              label="Other Reason"
              id="decline-other-reason"
              onChange={e => setDeclineDetails({...declineDetails, declineOtherReason: e.target.value})}
              value={declineDetails.declineOtherReason}
              required={declineDetails.declineReason === 'other'}
              margin="dense"
              variant="outlined"
              InputLabelProps={{shrink: true}}
              fullWidth
            />
          )}
        </Grid>
      </Grid>
    </QuotingToolModal>
  );
};

export const CopyQuoteModal = (props: QuotingToolModalProps) => {
  const dispatch = useDispatch();

  const {selectedQuotesForAction, actionSource, selectedSingleQuoteForAction} = useSelector(
    (state: ReducerState) => state.quoteActions
  );
  const {opportunitySummary} = useSelector((state: ReducerState) => state.opportunity);

  const selectedQuote =
    actionSource === QuoteActionSource.QuoteList && isDefined(selectedSingleQuoteForAction)
      ? selectedSingleQuoteForAction
      : Object.values(selectedQuotesForAction)[0];

  const handleContinue = () => {
    if (isLoaded(opportunitySummary) && selectedQuote?.effectiveDate) {
      dispatch(
        updateCopyQuoteDetails({
          requestPayload: {
            txEffectiveDate: DateTime.fromISO(selectedQuote?.effectiveDate ?? '').toISO(),
            customer: {
              _uri: opportunitySummary.Data?.customer?._uri ?? '',
            },
            opportunity: {
              _uri: getOpportunityURI(opportunitySummary.Data?._key?.rootId ?? ''),
            },
          },
        })
      );
      dispatch(toggleQuoteActionConfirmationModal(true));
    }
  };

  return (
    <QuotingToolModal
      {...props}
      onContinue={() => {
        handleContinue();
        props.onClose?.();
      }}
      disableContinue={!selectedQuote?.effectiveDate}
    >
      <Grid container direction="column" spacing={4}>
        <Grid item xs={8}>
          <KeyboardDatePicker
            format={'MM/dd/yyyy'}
            placeholder="MM/DD/YYYY"
            variant="inline"
            id="policy-effective-date"
            label="Policy Effective Date"
            onChange={() => {}}
            value={safeRenderDatePickerValue(selectedQuote?.effectiveDate)}
            disabled
            required
            margin="dense"
            InputLabelProps={{shrink: true}}
            inputVariant="outlined"
          />
        </Grid>
      </Grid>
    </QuotingToolModal>
  );
};

export const ActionConfirmationModal = (props: QuotingToolModalProps) => {
  const {
    selectedQuoteAction,
    actionSource,
    selectedSingleQuoteForAction,
    selectedQuotesForAction,
    quoteDeclineDetails,
    copyQuoteDetails,
  } = useSelector((state: ReducerState) => state.quoteActions);

  const {opportunitySummary} = useSelector((state: ReducerState) => state.opportunity);

  const dispatch = useDispatch();

  if (!isLoaded(opportunitySummary)) {
    return null;
  }

  const handleConfirmation = () => {
    const selectedQuotes =
      actionSource === QuoteActionSource.QuoteList && isDefined(selectedSingleQuoteForAction)
        ? [selectedSingleQuoteForAction]
        : Object.values(selectedQuotesForAction);

    if (
      selectedQuoteAction === QuoteListAction.DeclineByCompany ||
      selectedQuoteAction === QuoteListAction.DeclineByCustomer
    ) {
      dispatch(
        declineQuote(
          quoteDeclineDetails,
          selectedQuotes,
          opportunitySummary.Data?._key?.rootId ?? '',
          opportunitySummary.Data?._key?.revisionNo ?? 1
        )
      );
      dispatch(clearQuoteDeclineDetails());
    } else if (selectedQuoteAction === QuoteListAction.CopyQuote) {
      dispatch(
        copyQuote(
          copyQuoteDetails,
          selectedQuotes,
          opportunitySummary.Data?._key?.rootId ?? '',
          opportunitySummary.Data?._key?.revisionNo ?? 1
        )
      );
      dispatch(clearCopyQuoteDetails());
    }
  };

  return (
    <QuotingToolModal
      {...props}
      onContinue={() => {
        handleConfirmation();
        props.onClose?.();
      }}
      title={`Confirm ${actionLabel[selectedQuoteAction ?? '']}`}
    >
      <Typography component="strong">{confirmationText[selectedQuoteAction ?? '']}</Typography>
    </QuotingToolModal>
  );
};

type QuotesTabModalProps = {
  action: QuoteListAction | 'ConfirmQuoteAction' | undefined;
} & Partial<Pick<QuotingToolModalProps, 'open' | 'onClose' | 'onContinue'>>;

export const QuotesTabModal = ({action, ...modalProps}: QuotesTabModalProps) => {
  switch (action) {
    case QuoteListAction.DeclineByCompany:
    case QuoteListAction.DeclineByCustomer:
      return <DeclineQuoteModal {...modalProps} title={actionLabel[action]} />;
    case QuoteListAction.CopyQuote:
      return <CopyQuoteModal {...modalProps} title={actionLabel[action]} />;
    case 'ConfirmQuoteAction':
      return <ActionConfirmationModal {...modalProps} />;
    default:
      return <></>;
  }
};

const actionLabel: Record<string, string> = {
  [QuoteListAction.DeclineByCompany]: 'Decline By SIC',
  [QuoteListAction.DeclineByCustomer]: 'Decline By Broker/Group',
  [QuoteListAction.CopyQuote]: 'Copy from Quote',
};

const confirmationText: Record<string, string> = {
  [QuoteListAction.DeclineByCompany]: 'Are you sure the company wants to decline this quote?',
  [QuoteListAction.DeclineByCustomer]: 'Are you sure the customer wants to decline this quote?',
  [QuoteListAction.CopyQuote]: 'Are you sure you want to copy this quote?',
};
